import axios from "axios";
import firebase from "firebase/compat/app";
import { getPostHogContext } from "@/posthog";

const JWT_KEY = "katana_api_jwt";

export function apiBase() {
  return (process.env.VUE_APP_API_URL || "http://localhost:1712").replace(/\/$/, "");
}

const client = axios.create({
  baseURL: apiBase(),
});

client.interceptors.request.use((config) => {
  const { distinctId, sessionId } = getPostHogContext();
  if (distinctId) config.headers["X-POSTHOG-DISTINCT-ID"] = distinctId;
  if (sessionId) config.headers["X-POSTHOG-SESSION-ID"] = sessionId;
  return config;
});

export function clearApiSession() {
  sessionStorage.removeItem(JWT_KEY);
}

export async function ensureApiSession() {
  const existing = sessionStorage.getItem(JWT_KEY);
  if (existing) return existing;

  const user = firebase.auth().currentUser;
  if (!user) throw new Error("Not authenticated");

  const idToken = await user.getIdToken();
  const { data } = await client.post("/auth/session", { idToken });
  sessionStorage.setItem(JWT_KEY, data.token);
  return data.token;
}

export function apiPublic(config) {
  return client.request(config);
}

export async function apiUser(config) {
  const token = await ensureApiSession();
  try {
    return await client.request({
      ...config,
      headers: {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      },
    });
  } catch (error) {
    if (error.response?.status !== 401) throw error;
    clearApiSession();
    const retry = await ensureApiSession();
    return client.request({
      ...config,
      headers: {
        ...config.headers,
        Authorization: `Bearer ${retry}`,
      },
    });
  }
}

export function machineTokenValue(token) {
  return typeof token === "string" ? token : token?.fullToken;
}

export function apiMachine(token, config) {
  return client.request({
    ...config,
    headers: {
      ...config.headers,
      Authorization: `Bearer ${machineTokenValue(token)}`,
    },
  });
}

export const katanaApi = {
  apiBase,
  apiPublic,
  apiUser,
  apiMachine,
  ensureApiSession,
  clearApiSession,
  machineTokenValue,
};
